import React, { useMemo } from 'react';
import { User, Plus, Edit2, Mail, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { AnnouncementContactsCard } from '../../../components/AnnouncementContactsCard';
import { clientPortalService } from '../../../services/clientPortal.service';

export const ContactsTab = ({ account, canManageContacts = true }) => {
  const announcementApi = useMemo(() => ({
    list: () => clientPortalService.getAnnouncementContacts(),
    add: (payload) => clientPortalService.addAnnouncementContact(payload),
    update: (id, payload) => clientPortalService.updateAnnouncementContact(id, payload),
    remove: (id) => clientPortalService.deleteAnnouncementContact(id),
  }), []);

  return (
    <div className="space-y-6">
      <Card data-testid="contacts-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5 text-amber-500" />
            Contacts
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => toast.info('Add contact feature coming soon!')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </CardHeader>
        <CardContent>
          {!account.contacts || account.contacts.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No contacts configured</p>
            </div>
          ) : (
            <div className="space-y-4">
              {account.contacts.map((contact, index) => (
                <div
                  key={contact.email || contact.name || index}
                  className="p-4 border rounded-lg hover:bg-gray-50"
                  data-testid={`contact-${index}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{contact.name}</h3>
                        {contact.is_primary && (
                          <Badge className="bg-amber-100 text-amber-800">Primary</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{contact.role}</p>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="h-4 w-4" />
                          <a href={`mailto:${contact.email}`} className="hover:text-amber-600">
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-4 w-4" />
                          <a href={`tel:${contact.phone}`} className="hover:text-amber-600">
                            {contact.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toast.info('Edit contact feature coming soon!')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AnnouncementContactsCard
        api={announcementApi}
        canEdit={canManageContacts}
        testIdPrefix="client-announcement-contacts"
      />
    </div>
  );
};
